import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import styles from './LoginPage.module.css';

export default function LoginPage() {
  const { login } = useAuth();
  const navigate  = useNavigate();

  const [email,    setEmail]    = useState('');
  const [password, setPassword] = useState('');
  const [error,    setError]    = useState('');
  const [loading,  setLoading]  = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const res = await fetch('/api/auth/login', {
        method:  'POST',
        headers: { 'Content-Type': 'application/json' },
        body:    JSON.stringify({ email, password }),
      });
      const data = await res.json();
      if (!res.ok) {
        setError(data.detail || 'Login failed.');
        return;
      }
      login(data);
      const role = data.user.role;
      if (role === 'STUDENT') {
        navigate('/student');
      } else {
        navigate('/instructor');
      }
    } catch (err) {
      setError('Could not connect to the server.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className={styles.page}>
      <div className={styles.card}>
        {/* The product name is the page's heading. It was a div, so this page
            had no h1 at all and a screen reader's heading list was empty. */}
        <h1 className={styles.logo}>
          <span className={styles.logoText}>Gradiance</span>
          <span className={styles.logoDot} aria-hidden="true">.</span>
        </h1>
        <p className={styles.tagline}>Classroom queue management</p>
        <form onSubmit={handleSubmit} className={styles.form}>
          <div className={styles.field}>
            {/* htmlFor/id pairs the label to the field. Without it the label is
                just nearby text: a screen reader announces "edit text, blank"
                and clicking the label doesn't focus the input. */}
            <label htmlFor="login-email">School Email</label>
            <input
              id="login-email"
              name="email"
              type="email"
              // WCAG 1.3.5: lets browsers and assistive tech fill known values,
              // which matters most for people who find typing costly.
              autoComplete="email"
              placeholder="you@uwec.edu"
              value={email}
              onChange={e => setEmail(e.target.value)}
              required
              autoFocus
            />
          </div>
          <div className={styles.field}>
            <label htmlFor="login-password">Password</label>
            <input
              id="login-password"
              name="password"
              type="password"
              autoComplete="current-password"
              placeholder="••••••••"
              value={password}
              onChange={e => setPassword(e.target.value)}
              required
            />
          </div>
          {/* role="alert" so a failed sign-in is spoken. Otherwise the message
              appears silently and a screen reader user is left waiting on a
              page that looks, to them, unchanged. */}
          {error && (
            <div className={styles.error} role="alert">
              {error}
            </div>
          )}
          <button
            type="submit"
            className={`btn-primary ${styles.submitBtn}`}
            disabled={loading}
            aria-busy={loading}
          >
            {loading ? 'Signing in…' : 'Sign In'}
          </button>
        </form>
        <nav className={styles.footer} aria-label="Account help">
          <Link to="/forgot-password">Forgot password?</Link>
          <span className={styles.sep} aria-hidden="true">·</span>
          <Link to="/register/invite">Have an invite code?</Link>
          <span className={styles.sep} aria-hidden="true">·</span>
          {/* Plain <a>, not <Link>: the manuals are static HTML in public/, not
              routes. <Link> would be handled client-side and fall through to
              the SPA's catch-all. Same tab, and reachable signed out — someone
              who can't get past this screen is exactly who needs them.
              These replaced the docs.gradiance.org link: that site is now
              reached only by typing its URL directly. */}
          <a href="/manual/">Student Manual</a>
          <span className={styles.sep} aria-hidden="true">·</span>
          <a href="/manual/instructor/">Instructor Manual</a>
        </nav>
      </div>
    </div>
  );
}
